import { createFileRoute, Link, notFound, useRouter } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { detectLongWeekends, formatLongWeekendRange } from "@/lib/pseo";
import { getHolidaysForYear } from "@/data/holidays";
import {
  MONTH_NAMES_ID,
  MONTH_SLUGS_ID,
  parseBareMonthSlug,
  parseISO,
  parseYearSlug,
} from "@/lib/calendar/dates";
import { withSlash } from "@/lib/url";
import { MonthGrid, CalendarLegend } from "@/components/calendar/MonthGrid";

export const Route = createFileRoute("/harpitnas/$slug/$month")({
  loader: ({ params }) => {
    const y = parseYearSlug(params.slug);
    const m = parseBareMonthSlug(params.month);
    if (y === null || m === null) throw notFound();
    return { y, m };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return {};
    const { y, m } = loaderData;
    const monthName = MONTH_NAMES_ID[m - 1];
    const title = `Harpitnas ${monthName} ${y} - Long Weekend & Cuti Strategis`;
    const desc = `Daftar harpitnas dan long weekend di ${monthName} ${y}. Cek tanggal, cuti yang disarankan, dan harpitnas terdekat.`;
    const canonical = `https://hari.co.id${withSlash(`/harpitnas/${params.slug}/${params.month}`)}`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: canonical },
      ],
      links: [{ rel: "canonical", href: canonical }],
    };
  },
  component: HarpitnasMonth,
  notFoundComponent: () => (
    <SiteShell>
      <div className="py-16 text-center">
        <h1 className="text-2xl font-bold">Halaman tidak ditemukan</h1>
      </div>
    </SiteShell>
  ),
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <SiteShell>
        <div className="py-16 text-center">
          <p className="text-sm text-muted-foreground">{error.message}</p>
          <button onClick={() => { router.invalidate(); reset(); }} className="mt-4 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground">
            Coba lagi
          </button>
        </div>
      </SiteShell>
    );
  },
});

function HarpitnasMonth() {
  const { y, m } = Route.useLoaderData();
  const monthName = MONTH_NAMES_ID[m - 1];
  const all = detectLongWeekends(y, getHolidaysForYear(y));
  const inMonth = all.filter((x) => {
    const a = parseISO(x.startISO)!;
    return a.y === y && a.m === m;
  });
  const harp = inMonth.filter((x) => x.category === "harpitnas");
  const lw = inMonth.filter((x) => x.category === "long-weekend");

  // Nearest harpitnas if this month is empty (search across this year & adjacent year).
  const nearest = (() => {
    if (harp.length > 0) return [];
    const target = new Date(Date.UTC(y, m - 1, 15)).getTime();
    const pool: { dist: number; lw: typeof all[number] }[] = [];
    const yearsToScan = [y - 1, y, y + 1];
    for (const yr of yearsToScan) {
      const list = detectLongWeekends(yr, getHolidaysForYear(yr)).filter((x) => x.category === "harpitnas");
      for (const item of list) {
        const a = parseISO(item.startISO)!;
        const t = new Date(Date.UTC(a.y, a.m - 1, a.d)).getTime();
        pool.push({ dist: Math.abs(t - target), lw: item });
      }
    }
    pool.sort((a, b) => a.dist - b.dist);
    return pool.slice(0, 3).map((p) => p.lw);
  })();

  return (
    <SiteShell>
      <div className="mx-auto max-w-4xl py-8">
        <nav className="mb-4 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-primary">Beranda</Link>{" / "}
          <Link to={withSlash(`/harpitnas`) as never} className="hover:text-primary">Harpitnas</Link>{" / "}
          <Link to={withSlash(`/harpitnas/${y}`) as never} className="hover:text-primary">{y}</Link>{" / "}
          {monthName}
        </nav>
        <h1 className="font-display text-4xl tracking-tight md:text-5xl">
          Harpitnas {monthName} {y}
        </h1>
        <p className="mt-2 text-muted-foreground">
          Halaman ini merangkum {harp.length} harpitnas (hari kejepit nasional) dan{" "}
          {lw.length} long weekend di bulan {monthName} {y}, lengkap dengan kalender bulanan,
          rekomendasi cuti, serta tautan ke jadwal sholat dan hari penting terkait.
        </p>

        {harp.length === 0 ? (
          <section className="mt-8 rounded-lg border border-border bg-muted/30 p-5">
            <h2 className="text-lg font-semibold">
              Tidak ada harpitnas di {monthName} {y}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Berikut harpitnas terdekat yang bisa kamu rencanakan:
            </p>
            <ul className="mt-3 space-y-2">
              {nearest.map((x) => {
                const a = parseISO(x.startISO)!;
                return (
                  <li key={x.startISO}>
                    <Link
                      to={withSlash(`/harpitnas/${a.y}/${MONTH_SLUGS_ID[a.m - 1]}`) as never}
                      className="text-primary hover:underline"
                    >
                      {formatLongWeekendRange(x)}
                    </Link>{" "}
                    <span className="text-xs text-muted-foreground">({x.totalDays} hari)</span>
                  </li>
                );
              })}
            </ul>
          </section>
        ) : (
          <section className="mt-8 space-y-3">
            {harp.map((x) => (
              <article key={x.startISO} className="rounded-lg border border-border bg-card p-4">
                <div className="font-semibold">{formatLongWeekendRange(x)}</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  Total {x.totalDays} hari libur · cuti 1 hari
                  {x.harpitISO && ` (${x.harpitISO})`}
                </div>
                {x.triggers.length > 0 && (
                  <div className="mt-2 text-sm">
                    <span className="font-medium">Pemicu: </span>
                    {x.triggers.map((t) => t.name).join(", ")}
                  </div>
                )}
              </article>
            ))}
          </section>
        )}

        {lw.length > 0 && (
          <section className="mt-10">
            <h2 className="text-xl font-semibold">Long Weekend di {monthName} {y}</h2>
            <ul className="mt-3 space-y-2">
              {lw.map((x) => (
                <li key={x.startISO} className="rounded-md border border-border bg-card p-3 text-sm">
                  <span className="font-medium">{formatLongWeekendRange(x)}</span>
                  <span className="ml-2 text-xs text-muted-foreground">{x.totalDays} hari</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Calendar grid for the month, so users can see harpitnas in context. */}
        <section className="mt-10">
          <h2 className="text-xl font-semibold">
            Kalender {monthName} {y}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Tanggal merah, cuti bersama, dan hari penting di {monthName} {y}.
            Klik tanggal untuk detail lengkap (hijriah, weton, dan jadwal sholat).
          </p>
          <div className="mt-4">
            <MonthGrid year={y} month={m} />
          </div>
          <div className="mt-3">
            <CalendarLegend />
          </div>
          <div className="mt-3 flex flex-wrap gap-3 text-sm">
            <Link
              to={withSlash(`/kalender/${y}/${MONTH_SLUGS_ID[m - 1]}`) as never}
              className="rounded-md border border-border px-3 py-1.5 hover:bg-accent"
            >
              Buka kalender {monthName} {y} →
            </Link>
            <Link
              to={withSlash(`/libur-nasional/${y}`) as never}
              className="rounded-md border border-border px-3 py-1.5 hover:bg-accent"
            >
              Libur nasional {y}
            </Link>
            <Link
              to={withSlash(`/cuti-bersama/${y}`) as never}
              className="rounded-md border border-border px-3 py-1.5 hover:bg-accent"
            >
              Cuti bersama {y}
            </Link>
          </div>
        </section>

        {/* Unique pSEO copy: tips & FAQ, varies by harp count to stay non-duplicate. */}
        <section className="mt-10 grid gap-4 md:grid-cols-2">
          <article className="rounded-lg border border-border bg-card p-5">
            <h2 className="text-lg font-semibold">Tips Mengambil Cuti di {monthName} {y}</h2>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
              <li>
                Ajukan cuti minimal 2 minggu sebelum tanggal harpitnas agar pengaturan tim
                lebih leluasa.
              </li>
              <li>
                Kombinasikan harpitnas dengan akhir pekan untuk mendapatkan minimal 4 hari libur
                berturut-turut tanpa banyak menguras jatah cuti tahunan.
              </li>
              <li>
                Periksa kembali kalender resmi sebelum memesan tiket — SKB 3 Menteri kadang
                merevisi cuti bersama.
              </li>
              {harp.length === 0 && (
                <li>
                  Karena {monthName} {y} tidak memiliki harpitnas, manfaatkan long weekend di atas
                  atau geser cuti ke bulan terdekat.
                </li>
              )}
            </ul>
          </article>
          <article className="rounded-lg border border-border bg-card p-5">
            <h2 className="text-lg font-semibold">FAQ Harpitnas {monthName} {y}</h2>
            <dl className="mt-2 space-y-3 text-sm">
              <div>
                <dt className="font-medium">Apa itu harpitnas?</dt>
                <dd className="text-muted-foreground">
                  Harpitnas adalah hari kerja yang terjepit di antara hari libur nasional
                  dan akhir pekan. Mengambil cuti 1 hari di harpitnas membuat libur Anda
                  jadi 4 hari atau lebih.
                </dd>
              </div>
              <div>
                <dt className="font-medium">
                  Berapa harpitnas di {monthName} {y}?
                </dt>
                <dd className="text-muted-foreground">
                  Bulan ini memiliki {harp.length} harpitnas dan {lw.length} long weekend
                  berdasarkan data libur nasional {y}.
                </dd>
              </div>
              <div>
                <dt className="font-medium">Apakah harpitnas otomatis libur?</dt>
                <dd className="text-muted-foreground">
                  Tidak. Harpitnas tetap hari kerja kecuali pemerintah menetapkannya
                  sebagai cuti bersama lewat SKB 3 Menteri.
                </dd>
              </div>
            </dl>
          </article>
        </section>

        <div className="mt-10 flex justify-between text-sm">
          {(() => {
            const prev = m === 1 ? { y: y - 1, m: 12 } : { y, m: m - 1 };
            const next = m === 12 ? { y: y + 1, m: 1 } : { y, m: m + 1 };
            return (
              <>
                <Link
                  to={withSlash(`/harpitnas/${prev.y}/${MONTH_SLUGS_ID[prev.m - 1]}`) as never}
                  className="rounded-md border border-border px-3 py-1.5 hover:bg-accent"
                >
                  ← {MONTH_NAMES_ID[prev.m - 1]} {prev.y}
                </Link>
                <Link
                  to={withSlash(`/harpitnas/${next.y}/${MONTH_SLUGS_ID[next.m - 1]}`) as never}
                  className="rounded-md border border-border px-3 py-1.5 hover:bg-accent"
                >
                  {MONTH_NAMES_ID[next.m - 1]} {next.y} →
                </Link>
              </>
            );
          })()}
        </div>
      </div>
    </SiteShell>
  );
}